import {
  ASSIGNABLE_ROLES,
  type GrantValue,
  type MemberPermissions,
  type ModuleLevels,
  TEAM_WEB_PATHS,
  templatePermissions,
} from '@fos/shared/contracts/team';
import {
  type AccessLevel,
  type ApprovesAi,
  DATA_SCOPES,
  type DataScope,
  type GrantLevel,
  type GrantResourceType,
  type MembershipFlags,
  MODULES,
  PERMISSION_TEMPLATES,
  type PermissionTemplate,
  type Role,
} from '@fos/shared/enums';
import {
  DATA_SCOPE_LABELS,
  FLAG_LABELS,
  MEMBERSHIP_STATUS_LABELS,
  MEMBERSHIP_STATUS_TONES,
  MODULE_LABELS,
  PERMISSION_TEMPLATE_LABELS,
  ROLE_LABELS,
  TEAM_ACCESS_LEVEL_SHORT,
  TEAM_AI_LIMIT_NOTE,
  TEAM_AI_LIMIT_PARTNER_NOTE,
  TEAM_DATA_SCOPE_DESCRIPTIONS,
  TEAM_FLAG_DESCRIPTIONS,
  TEAM_PERMISSION_TEMPLATE_DESCRIPTIONS,
  TEAM_VAULT_NOTE,
} from '@fos/shared/labels';
import { Info, ShieldCheck, UserCheck, UserX } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Avatar } from '../../../ui/Avatar';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field, Label } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Breadcrumbs } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { Table, TBody, Td, Tr } from '../../../ui/Table';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { ChoiceCards } from './components/ChoiceCards';
import { ClientColorDot, type PickedItem, ResourcePicker } from './components/ResourcePicker';
import { SwitchRow } from './components/SwitchRow';
import { aiLimitInputAfterTemplate, aiLimitToInput, parseAiLimit, toastMutationError } from './team-ui';

/**
 * Ustawienia → Zespół → „Uprawnienia osoby” (/ustawienia/zespol/:membershipId). Czeka na projekt w Figmie
 * (DESIGN §7): istniejące wzorce. Sekcje: Rola, Szablon, Moduły (Brak/Podgląd/Edycja), Zakres danych (klienci,
 * pojedyncze projekty i zasoby), Uprawnienia specjalne (z sejfem), Limit AI. Zapis z potwierdzeniem hasłem.
 */

interface FormState {
  role: Role;
  template: PermissionTemplate | null;
  modules: ModuleLevels;
  dataScope: DataScope;
  flags: MembershipFlags;
  aiLimit: string;
  grants: GrantValue[];
}

type BooleanMemberFlag = Exclude<keyof MembershipFlags, 'approvesAi'>;
const USER_FLAGS: BooleanMemberFlag[] = [
  'seesRates',
  'sendsEmails',
  'preparesBilling',
  'issuesInvoices',
  'deploysProduction',
  'exportsData',
];

const LEVEL_OPTIONS = (['none', 'view', 'edit'] as const).map((l) => ({ value: l, label: TEAM_ACCESS_LEVEL_SHORT[l] }));
const GRANT_LEVEL_OPTIONS = [
  { value: 'view' as GrantLevel, label: 'Podgląd' },
  { value: 'edit' as GrantLevel, label: 'Edycja' },
];
const APPROVES_OPTIONS = [
  { value: 'none' as ApprovesAi, label: 'Żadne' },
  { value: 'own' as ApprovesAi, label: 'Swoje' },
];
const TEMPLATE_OPTIONS = [
  ...PERMISSION_TEMPLATES.map((t) => ({ value: t as string, label: PERMISSION_TEMPLATE_LABELS[t] })),
  { value: 'custom', label: 'Własne ustawienia' },
];

function fromData(d: MemberPermissions): FormState {
  return {
    role: d.role,
    template: d.template,
    modules: { ...d.modules },
    dataScope: d.dataScope,
    flags: { ...d.flags },
    aiLimit: aiLimitToInput(d.aiMonthlyLimitPln),
    grants: d.grants
      .filter((g) => !g.missing)
      .map((g) => ({ resourceType: g.resourceType, resourceId: g.resourceId, level: g.level })),
  };
}

function permissionsKey(f: FormState): string {
  const grants = [...f.grants].sort((a, b) =>
    `${a.resourceType}${a.resourceId}`.localeCompare(`${b.resourceType}${b.resourceId}`),
  );
  return JSON.stringify([f.template, f.modules, f.dataScope, f.flags, parseAiLimit(f.aiLimit).value, grants]);
}

export function MemberPermissionsPage() {
  const { membershipId = '' } = useParams();
  const session = useActiveSession();
  const actorRole = session.membership?.role ?? null;
  const utils = trpc.useUtils();
  const member = trpc.team.getMemberPermissions.useQuery({ membershipId }, { enabled: !!membershipId });
  const options = trpc.team.grantOptions.useQuery(undefined, { enabled: member.isSuccess, staleTime: 60_000 });
  const updateRole = trpc.team.updateRole.useMutation();
  const updatePermissions = trpc.team.updatePermissions.useMutation();
  const deactivate = trpc.team.deactivate.useMutation();
  const reactivate = trpc.team.reactivate.useMutation();

  const data = member.data;
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: formularz od nowa tylko po zmianie wersji osoby
  useEffect(() => {
    if (data) {
      setForm(fromData(data));
      setConflict(false);
    }
  }, [data?.membershipId, data?.version]);

  const initial = useMemo(() => (data ? fromData(data) : null), [data]);
  const labels = useMemo(() => {
    const map = new Map<string, { label: string; description: string | null; color: string | null }>();
    for (const c of options.data?.clients ?? [])
      map.set(`client:${c.id}`, { label: c.name, description: null, color: c.color });
    for (const p of options.data?.projects ?? []) {
      map.set(`project:${p.id}`, { label: p.name, description: p.clientName ?? 'Projekt wewnętrzny', color: p.color });
    }
    for (const a of options.data?.assets ?? []) {
      const entry = {
        label: a.name,
        description: [a.clientName, a.address].filter(Boolean).join(' · ') || null,
        color: a.color,
      };
      map.set(`asset:${a.id}`, entry);
      map.set(`vault_asset:${a.id}`, entry);
    }
    for (const g of data?.grants ?? []) {
      map.set(`${g.resourceType}:${g.resourceId}`, { label: g.label, description: g.description, color: g.color });
    }
    return map;
  }, [options.data, data]);

  if (!membershipId || member.isPending || (member.isSuccess && !form)) {
    return (
      <div role="status" aria-label="Wczytywanie">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-6 h-8 w-64" />
        <Skeleton className="mt-10 h-40 w-full" />
      </div>
    );
  }
  if (member.isError) {
    const code = appErrorCode(member.error);
    if (code === 'FORBIDDEN') {
      return <EmptyState icon={ShieldCheck} title="Tylko administrator zarządza zespołem" />;
    }
    if (code === 'NOT_FOUND') {
      return (
        <EmptyState
          title="Nie znaleziono tej osoby w zespole"
          action={
            <Button asChild variant="secondary" className={TOUCH_BUTTON}>
              <Link to={TEAM_WEB_PATHS.team}>Wróć do zespołu</Link>
            </Button>
          }
        />
      );
    }
    return <ErrorState onRetry={() => void member.refetch()} retrying={member.isFetching} />;
  }
  if (!data || !form || !initial) return null;

  const ownerLocked = data.role === 'owner' && actorRole !== 'owner';
  const editable = !ownerLocked;
  const isUser = form.role === 'user';
  const roleDirty = form.role !== data.role;
  const permsDirty = permissionsKey(form) !== permissionsKey(initial);
  const dirty = roleDirty || permsDirty;
  const limit = parseAiLimit(form.aiLimit);
  const saving = updateRole.isPending || updatePermissions.isPending;

  const patch = (p: Partial<FormState>) => setForm((f) => (f ? { ...f, ...p } : f));
  const grantsOf = (type: GrantResourceType) => form.grants.filter((g) => g.resourceType === type);
  const addGrant = (type: GrantResourceType, id: string, level: GrantLevel) =>
    patch({ grants: [...form.grants, { resourceType: type, resourceId: id, level }] });
  const removeGrant = (type: GrantResourceType, id: string) =>
    patch({ grants: form.grants.filter((g) => !(g.resourceType === type && g.resourceId === id)) });
  const setGrantLevel = (type: GrantResourceType, id: string, level: GrantLevel) =>
    patch({
      grants: form.grants.map((g) => (g.resourceType === type && g.resourceId === id ? { ...g, level } : g)),
    });

  const picked = (type: GrantResourceType, withLevel: boolean): PickedItem[] =>
    grantsOf(type).map((g) => {
      const info = labels.get(`${type}:${g.resourceId}`);
      return {
        id: g.resourceId,
        label: info?.label ?? 'Wczytywanie…',
        description: info?.description ?? null,
        color: info?.color ?? null,
        trailing: withLevel ? (
          <Select
            size="s"
            aria-label={`Poziom dostępu: ${info?.label ?? ''}`}
            value={g.level}
            onValueChange={(level) => setGrantLevel(type, g.resourceId, level)}
            options={GRANT_LEVEL_OPTIONS}
            disabled={!editable}
            className="w-[116px]"
          />
        ) : undefined,
      };
    });

  const available = (type: GrantResourceType) => {
    const taken = new Set(grantsOf(type).map((g) => g.resourceId));
    if (type === 'client') {
      return (options.data?.clients ?? [])
        .filter((c) => !taken.has(c.id))
        .map((c) => ({ value: c.id, label: c.name, leading: <ClientColorDot color={c.color} /> }));
    }
    if (type === 'project') {
      return (options.data?.projects ?? [])
        .filter((p) => !taken.has(p.id))
        .map((p) => ({
          value: p.id,
          label: p.name,
          description: p.clientName ?? 'wewnętrzny',
          leading: <ClientColorDot color={p.color} />,
        }));
    }
    return (options.data?.assets ?? [])
      .filter((a) => !taken.has(a.id))
      .map((a) => ({
        value: a.id,
        label: a.name,
        description: a.clientName ?? undefined,
        leading: <ClientColorDot color={a.color} />,
      }));
  };

  // szablon Partner wpisuje limit AI 0 zł (AI we własnym workspace'ie); odejście od niego czyści limit z szablonu
  const applyTemplate = (value: string) => {
    if (value === 'custom') {
      patch({ template: null, aiLimit: aiLimitInputAfterTemplate(form.template, null, form.aiLimit) });
      return;
    }
    const t = value as PermissionTemplate;
    const tp = templatePermissions(t);
    patch({
      template: t,
      modules: tp.modules,
      dataScope: tp.dataScope,
      flags: tp.flags,
      aiLimit: aiLimitInputAfterTemplate(form.template, t, form.aiLimit),
      grants: tp.dataScope === 'all' ? form.grants.filter((g) => g.resourceType === 'vault_asset') : form.grants,
    });
  };

  const save = async () => {
    setFormError(null);
    setConflict(false);
    if (limit.error) return;
    try {
      let version = data.version;
      // rola `client` (portal klienta) nie występuje w Zespole — tu jej nie ma (zawężenie typu dla `team.updateRole`)
      if (roleDirty && form.role !== 'client') {
        const res = await updateRole.mutateAsync({ membershipId: data.membershipId, role: form.role, version });
        version = res.version;
      }
      if (permsDirty) {
        await updatePermissions.mutateAsync({
          membershipId: data.membershipId,
          version,
          template: form.role === 'user' ? form.template : null,
          modules: form.modules,
          dataScope: form.dataScope,
          // Użytkownik: „Zatwierdza akcje AI” najwyżej „Swoje” (API odrzuca „Wszystkie”; przełącznik pokazuje „Swoje”)
          flags:
            form.role === 'user' && form.flags.approvesAi === 'all' ? { ...form.flags, approvesAi: 'own' } : form.flags,
          aiMonthlyLimitPln: limit.value,
          grants: form.grants,
        });
      }
      toast.success('Zapisano uprawnienia', { description: `${data.name}: zmiany działają od następnego kliknięcia.` });
      await Promise.all([member.refetch(), utils.team.overview.invalidate()]);
    } catch (err) {
      const code = appErrorCode(err);
      if (code === 'STEP_UP_REQUIRED') return;
      if (code === 'CONFLICT') {
        setConflict(true);
        return;
      }
      setFormError(validationIssues(err)[0]?.message ?? errorMessage(err));
    }
  };

  const toggleStatus = async () => {
    try {
      if (data.status === 'active') {
        await deactivate.mutateAsync({ membershipId: data.membershipId, version: data.version });
        toast.success('Dostęp wyłączony', { description: 'Osoba została wylogowana z tego workspace’u.' });
      } else {
        await reactivate.mutateAsync({ membershipId: data.membershipId, version: data.version });
        toast.success('Dostęp włączony', { description: 'Osoba może się znowu zalogować.' });
      }
      setConfirmOff(false);
      await Promise.all([member.refetch(), utils.team.overview.invalidate()]);
    } catch (err) {
      setConfirmOff(false);
      toastMutationError(err);
    }
  };

  return (
    <div className="pb-24">
      <Breadcrumbs items={[{ label: 'Zespół', to: TEAM_WEB_PATHS.team }, { label: data.name }]} />

      <header className="mt-4 flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Avatar initials={data.initials} color={data.avatarColor} size={40} />
          <div className="min-w-0">
            <h2 className="flex items-baseline gap-2 truncate text-h3 text-fg">
              {data.name}
              {data.isSelf && <span className="text-body-s font-normal text-fg-muted">Ty</span>}
            </h2>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-s text-fg-secondary">
              <span className="truncate">{data.email}</span>
              <Status tone={MEMBERSHIP_STATUS_TONES[data.status]}>{MEMBERSHIP_STATUS_LABELS[data.status]}</Status>
              <span className="text-fg-muted">
                {data.lastLoginAt ? `ostatnio ${formatRelativePast(data.lastLoginAt)}` : 'jeszcze bez logowania'}
              </span>
            </p>
          </div>
        </div>
        {!data.isSelf && data.role !== 'owner' && (
          <Button
            variant="secondary"
            icon={data.status === 'active' ? UserX : UserCheck}
            loading={reactivate.isPending}
            onClick={() => (data.status === 'active' ? setConfirmOff(true) : void toggleStatus())}
            className={TOUCH_BUTTON}
          >
            {data.status === 'active' ? 'Wyłącz dostęp' : 'Włącz dostęp'}
          </Button>
        )}
      </header>

      {ownerLocked && (
        <p className="mt-6 flex items-center gap-2 rounded-lg border border-line bg-panel px-4 py-3 text-body-s text-fg-secondary">
          <Info size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
          Ustawienia właściciela zmienia tylko właściciel.
        </p>
      )}

      <Block title="Rola">
        {data.role === 'owner' ? (
          <p className="text-body-s text-fg-secondary">
            Właściciel ma pełny dostęp. Własność przekażesz w przyszłości (przekazanie własności).
          </p>
        ) : (
          <div className="max-w-[320px]">
            <Field hint={data.isSelf ? 'Własnej roli nie zmienisz.' : undefined}>
              <Select
                aria-label="Rola"
                value={form.role}
                disabled={!editable || data.isSelf}
                onValueChange={(role) =>
                  patch({
                    role,
                    // szablon (i jego limit AI) obowiązuje tylko przy roli Użytkownik
                    aiLimit: aiLimitInputAfterTemplate(
                      form.role === 'user' ? form.template : null,
                      role === 'user' ? form.template : null,
                      form.aiLimit,
                    ),
                  })
                }
                options={[...ASSIGNABLE_ROLES].reverse().map((r) => ({ value: r as Role, label: ROLE_LABELS[r] }))}
              />
            </Field>
          </div>
        )}
      </Block>

      {isUser ? (
        <>
          <Block
            title="Szablon"
            description="Punkt startowy: ustawia moduły, zakres i przełączniki. Potem zmienisz pojedyncze pozycje."
          >
            <div className="max-w-[320px]">
              <Field hint={form.template ? TEAM_PERMISSION_TEMPLATE_DESCRIPTIONS[form.template] : undefined}>
                <Select
                  aria-label="Szablon uprawnień"
                  value={form.template ?? 'custom'}
                  disabled={!editable}
                  onValueChange={applyTemplate}
                  options={TEMPLATE_OPTIONS}
                />
              </Field>
            </div>
          </Block>

          <Block title="Moduły" description="„Dziś” jest zawsze i pokazuje tylko to, do czego osoba ma dostęp.">
            <Table aria-label="Dostęp do modułów">
              <TBody>
                {MODULES.map((m) => (
                  <Tr key={m}>
                    <Td className="h-12">{MODULE_LABELS[m]}</Td>
                    <Td align="right" className="h-12">
                      <Segmented
                        size="s"
                        aria-label={`Dostęp: ${MODULE_LABELS[m]}`}
                        value={form.modules[m]}
                        onValueChange={(level: AccessLevel) => {
                          if (editable) patch({ modules: { ...form.modules, [m]: level } });
                        }}
                        options={LEVEL_OPTIONS}
                      />
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </Block>

          <Block title="Zakres danych">
            <ChoiceCards
              aria-label="Zakres danych"
              value={form.dataScope}
              disabled={!editable}
              onValueChange={(dataScope) =>
                patch({
                  dataScope,
                  grants:
                    dataScope === 'all' ? form.grants.filter((g) => g.resourceType === 'vault_asset') : form.grants,
                })
              }
              options={DATA_SCOPES.map((s) => ({
                value: s,
                label: DATA_SCOPE_LABELS[s],
                description: TEAM_DATA_SCOPE_DESCRIPTIONS[s],
              }))}
            />
            {form.dataScope !== 'all' && (
              <div className="mt-6 flex flex-col gap-6">
                <div className="flex flex-col gap-1.5">
                  <Label>{form.dataScope === 'selected' ? 'Wybrani klienci' : 'Dodatkowo udostępnieni klienci'}</Label>
                  <ResourcePicker
                    aria-label="Udostępnieni klienci"
                    items={picked('client', false)}
                    options={available('client')}
                    onAdd={(id) => addGrant('client', id, 'edit')}
                    onRemove={(id) => removeGrant('client', id)}
                    placeholder="Dodaj klienta…"
                    disabled={!editable}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Pojedyncze projekty</Label>
                  <p className="text-caption text-fg-muted">
                    Dostęp do projektu bez reszty klienta (karta klienta tylko do podglądu).
                  </p>
                  <ResourcePicker
                    aria-label="Udostępnione projekty"
                    layout="rows"
                    items={picked('project', true)}
                    options={available('project')}
                    onAdd={(id) => addGrant('project', id, 'edit')}
                    onRemove={(id) => removeGrant('project', id)}
                    placeholder="Dodaj projekt…"
                    disabled={!editable}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Pojedyncze zasoby Utrzymania</Label>
                  <ResourcePicker
                    aria-label="Udostępnione zasoby"
                    layout="rows"
                    items={picked('asset', true)}
                    options={available('asset')}
                    onAdd={(id) => addGrant('asset', id, 'view')}
                    onRemove={(id) => removeGrant('asset', id)}
                    placeholder="Dodaj zasób…"
                    disabled={!editable}
                  />
                </div>
              </div>
            )}
          </Block>
        </>
      ) : (
        <Block title="Moduły i zakres danych">
          <p className="text-body-s text-fg-secondary">
            Rola {ROLE_LABELS[form.role]} ma edycję wszystkich modułów i widzi wszystkich klientów.
            {form.role === 'manager' && ' Nie zaprasza osób i nie zmienia ról.'}
          </p>
        </Block>
      )}

      <Block title="Uprawnienia specjalne">
        {isUser && (
          <div className="border-t border-line">
            {USER_FLAGS.map((flag) => (
              <SwitchRow
                key={flag}
                label={FLAG_LABELS[flag]}
                description={TEAM_FLAG_DESCRIPTIONS[flag]}
                checked={form.flags[flag]}
                disabled={!editable}
                onCheckedChange={(checked) => patch({ flags: { ...form.flags, [flag]: checked } })}
              />
            ))}
            <div className="flex flex-col gap-3 border-b border-line py-3 md:flex-row md:items-start">
              <div className="min-w-0 flex-1">
                <p className="text-body-s font-medium text-fg">{FLAG_LABELS.approvesAi}</p>
                <p className="mt-0.5 text-caption text-fg-muted">{TEAM_FLAG_DESCRIPTIONS.approvesAi}</p>
              </div>
              <Segmented
                size="s"
                aria-label={FLAG_LABELS.approvesAi}
                value={form.flags.approvesAi === 'all' ? 'own' : form.flags.approvesAi}
                onValueChange={(approvesAi: ApprovesAi) => {
                  if (editable) patch({ flags: { ...form.flags, approvesAi } });
                }}
                options={APPROVES_OPTIONS}
              />
            </div>
          </div>
        )}
        {(isUser || form.role === 'manager') && (
          <div className={isUser ? '' : 'border-t border-line'}>
            <SwitchRow
              label={FLAG_LABELS.vaultAccess}
              description={TEAM_FLAG_DESCRIPTIONS.vaultAccess}
              note={TEAM_VAULT_NOTE}
              checked={form.flags.vaultAccess}
              disabled={!editable}
              onCheckedChange={(vaultAccess) => patch({ flags: { ...form.flags, vaultAccess } })}
            />
            <div className="mt-4 flex flex-col gap-1.5">
              <Label>Dostępy (sejf) pojedynczych zasobów</Label>
              <ResourcePicker
                aria-label="Sejf pojedynczych zasobów"
                layout="rows"
                items={picked('vault_asset', false)}
                options={available('vault_asset')}
                onAdd={(id) => addGrant('vault_asset', id, 'view')}
                onRemove={(id) => removeGrant('vault_asset', id)}
                placeholder="Dodaj zasób z sejfem…"
                disabled={!editable}
              />
            </div>
            {form.role === 'manager' && (
              <p className="mt-3 text-caption text-fg-muted">Pozostałe uprawnienia wynikają z roli Manager.</p>
            )}
          </div>
        )}
        {!isUser && form.role !== 'manager' && (
          <p className="text-body-s text-fg-secondary">
            Rola {ROLE_LABELS[form.role]} ma wszystkie uprawnienia specjalne, w tym podgląd dostępów z sejfu.
          </p>
        )}
      </Block>

      <Block title="Limit AI">
        <div className="flex max-w-[480px] items-start justify-between gap-4">
          <div className="min-w-0">
            <Label htmlFor="member-ai-limit">Limit AI miesięcznie</Label>
            <p className="mt-0.5 text-caption text-fg-muted">
              {form.role === 'user' && form.template === 'partner'
                ? TEAM_AI_LIMIT_PARTNER_NOTE
                : `${TEAM_AI_LIMIT_NOTE} Puste = bez limitu osobistego (obowiązuje limit firmy).`}
            </p>
            {limit.error && (
              <p role="alert" className="mt-1 text-caption text-danger">
                {limit.error}
              </p>
            )}
          </div>
          <div className="relative w-28 shrink-0">
            <Input
              id="member-ai-limit"
              mono
              inputMode="decimal"
              placeholder="—"
              disabled={!editable}
              aria-invalid={limit.error ? true : undefined}
              value={form.aiLimit}
              onChange={(e) => patch({ aiLimit: e.target.value })}
              className="pr-8 text-right"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-caption text-fg-muted"
            >
              zł
            </span>
          </div>
        </div>
      </Block>

      <PartnerRateBlock userId={data.userId} editable={editable} />

      {(formError || conflict) && (
        <div className="mt-8">
          {conflict ? (
            <FormMessage>
              Ktoś zmienił uprawnienia tej osoby w międzyczasie.{' '}
              <button
                type="button"
                className="font-medium text-accent hover:underline"
                onClick={() => {
                  setConflict(false);
                  void member.refetch().then((r) => r.data && setForm(fromData(r.data)));
                }}
              >
                Wczytaj aktualne
              </button>
            </FormMessage>
          ) : (
            <FormMessage>{formError}</FormMessage>
          )}
        </div>
      )}

      {editable && (
        <div className="sticky bottom-0 z-10 -mx-4 mt-10 flex items-center justify-end gap-2 border-t border-line bg-app px-4 py-3 md:mx-0 md:px-0">
          <p className="mr-auto text-caption text-fg-muted" aria-live="polite">
            {dirty ? 'Masz niezapisane zmiany. Zapis poprosi o potwierdzenie hasłem.' : 'Wszystko zapisane.'}
          </p>
          <Button variant="ghost" disabled={!dirty || saving} onClick={() => setForm(initial)} className={TOUCH_BUTTON}>
            Odrzuć
          </Button>
          <Button
            variant="primary"
            disabled={!dirty || !!limit.error}
            loading={saving}
            onClick={() => void save()}
            className={TOUCH_BUTTON}
          >
            Zapisz zmiany
          </Button>
        </div>
      )}

      <Dialog
        open={confirmOff}
        onOpenChange={setConfirmOff}
        width="s"
        title={`Wyłączyć dostęp: ${data.name}?`}
        description="Osoba zostanie od razu wylogowana z tego workspace’u, a jej tokeny API przestaną działać. Zadania i wpisy czasu zostają. Dostęp możesz włączyć ponownie."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOff(false)} className={TOUCH_BUTTON}>
              Anuluj
            </Button>
            <Button
              variant="primary"
              icon={UserX}
              loading={deactivate.isPending}
              onClick={() => void toggleStatus()}
              className={TOUCH_BUTTON}
            >
              Wyłącz dostęp
            </Button>
          </>
        }
      />
    </div>
  );
}

/**
 * Domyślna stawka partnera (DECISIONS 2026-10-08 [P] „Stawka partnera”): ile prowadzący płaci tej osobie za godzinę,
 * gdy projekt nie ma jej własnej stawki. Zapis osobno od uprawnień (bez potwierdzenia hasłem, to nie zmiana dostępu);
 * obejmuje nierozliczone wpisy osoby w projektach bez stawki w projekcie. Osoba widzi swoją stawkę przy własnych wpisach.
 */
function PartnerRateBlock({ userId, editable }: { userId: string; editable: boolean }) {
  const rate = trpc.time.memberPartnerRate.useQuery({ userId });
  const save = trpc.time.setMemberPartnerRate.useMutation();
  const utils = trpc.useUtils();
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!rate.data) return null;
  const stored = rate.data.defaultPartnerRate ? rate.data.defaultPartnerRate.replace(/\.00$/, '') : '';
  const value = text ?? stored;
  const canEdit = editable && rate.data.canEdit;
  const submit = async () => {
    const normalized = value.trim().replace(',', '.').replace(/\s/g, '');
    if (normalized && !/^\d{1,8}(\.\d{1,2})?$/.test(normalized)) {
      setError('Wpisz stawkę, np. 120 albo 120,50');
      return;
    }
    setError(null);
    try {
      const res = await save.mutateAsync({ userId, defaultPartnerRate: normalized || null });
      await Promise.all([utils.time.invalidate(), utils.projects.invalidate()]);
      setText(null);
      toast.success(
        res.updatedEntries > 0
          ? `Zapisano stawkę partnera (zmieniona w ${res.updatedEntries} nierozliczonych wpisach)`
          : 'Zapisano stawkę partnera',
      );
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się zapisać stawki.'));
    }
  };
  return (
    <Block title="Stawka partnera">
      <div className="flex max-w-[480px] items-start justify-between gap-4">
        <div className="min-w-0">
          <Label htmlFor="member-partner-rate">Domyślna stawka partnera</Label>
          <p className="mt-0.5 text-caption text-fg-muted">
            Ile płacisz tej osobie za godzinę (koszt, nie cena dla klienta). Projekt może mieć własną stawkę. Puste =
            bez stawki.
          </p>
          {error && (
            <p role="alert" className="mt-1 text-caption text-danger">
              {error}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="relative w-28">
            <Input
              id="member-partner-rate"
              mono
              inputMode="decimal"
              placeholder="—"
              disabled={!canEdit}
              aria-invalid={error ? true : undefined}
              value={value}
              onChange={(e) => setText(e.target.value)}
              className="pr-11 text-right"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-caption text-fg-muted"
            >
              zł/h
            </span>
          </div>
          {canEdit && text !== null && text !== stored && (
            <Button variant="secondary" loading={save.isPending} onClick={() => void submit()} className={TOUCH_BUTTON}>
              Zapisz
            </Button>
          )}
        </div>
      </div>
    </Block>
  );
}

/** Sekcja uprawnień = biały blok (ui/Section, DECISIONS 2026-10-07 „Nowy układ wizualny”). */
function Block({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <Section className="mt-6" aria-label={title}>
      <h3 className="text-body-s font-semibold text-fg">{title}</h3>
      {description && <p className="mt-0.5 text-caption text-fg-muted">{description}</p>}
      <div className="mt-3">{children}</div>
    </Section>
  );
}
